import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { useQueryClient } from '@tanstack/react-query'
import IntegrationAuthProvider from '../../../IntegrationAuthProvider/IntegrationAuthProvider'
import Icon from '../../../../atoms/Icon'
import MarkupPreview from '../../../../atoms/MarkupPreview'
import Loading from '../../../../molecules/Loading'
import FlexRow from '../../../../molecules/FlexRow'
import FadeIn from '../../../../molecules/Transitions/FadeIn'
import SydButton from '../../../../commonDesign/Button'
import { getWorkflowStatus } from '../shared/redtailUIHelpers'
import { LinkedContactRow } from '../shared/LinkedContactDisplay'
import useRedtailListPreset from './useRedtailListPreset'
import CreateMeetingModal from './CreateMeetingModal'
import MeetingDetailModal from './MeetingDetailModal'
import CreateNoteModal from './CreateNoteModal'
import NoteDetailModal from './NoteDetailModal'
import WorkflowDetailModal from './WorkflowDetailModal'
import { formatMeetingDate, formatMeetingTime } from './meetingHelpers'
import { workflowDetailKey } from './useWorkflowDetail'

const SIZE_STYLES = {
  sm: { titleFontSize: '0.875rem', rowPadding: '8px 0', fontSize: 'var(--font-size-sm)', minHeight: '120px' },
  md: { titleFontSize: '1rem', rowPadding: '10px 0', fontSize: 'var(--font-size-sm)', minHeight: '180px' },
  lg: { titleFontSize: '1.25rem', rowPadding: '12px 0', fontSize: 'var(--font-size-md)', minHeight: '240px' }
}

/* ── Meetings ──────────────────────────────────────────────────────── */

function MeetingRow ({ activity, sizes, onClick }) {
  const datePart = formatMeetingDate(activity.start_date)
  const timePart = formatMeetingTime(activity)

  return (
    <div
      role='button'
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } }}
      style={{ borderBottom: '1px solid var(--color-neutral-200)', padding: sizes.rowPadding, cursor: 'pointer' }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
        <div style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          paddingTop: '2px',
          color: 'var(--text-color-subtle)'
        }}
        >
          <Icon name='calendar' customSize='20px' />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-color-subtle)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          >
            <LinkedContactRow contacts={activity.linked_contacts} />
          </div>
          <div style={{
            fontSize: sizes.fontSize,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          >
            {activity.subject || 'Untitled Meeting'}
          </div>
        </div>
        <div style={{
          flexShrink: 0,
          textAlign: 'right',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--text-color-subtle)',
          whiteSpace: 'nowrap'
        }}
        >
          {datePart ? <div>{datePart}</div> : null}
          {timePart ? <div>{timePart}</div> : null}
        </div>
      </div>
    </div>
  )
}

MeetingRow.propTypes = {
  activity: PropTypes.object.isRequired,
  sizes: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired
}

function MeetingsContent ({ items, sizes, members, config, createOpen, onCloseCreate }) {
  const [selectedActivity, setSelectedActivity] = useState(null)

  const handleCloseDetail = useCallback(() => setSelectedActivity(null), [])

  return (
    <>
      {!items.length
        ? (
          <FlexRow
            className='justify-center items-center'
            style={{
              minHeight: '80px',
              color: 'var(--text-color-subtle)',
              fontSize: 'var(--font-size-sm)'
            }}
          >
            No upcoming meetings
          </FlexRow>
        )
        : items.map(activity => (
          <MeetingRow
            key={activity.id}
            activity={activity}
            sizes={sizes}
            onClick={() => setSelectedActivity(activity)}
          />
        ))}

      <CreateMeetingModal
        open={createOpen}
        onClose={onCloseCreate}
        config={config}
        members={members}
      />

      <MeetingDetailModal
        open={!!selectedActivity}
        onClose={handleCloseDetail}
        activity={selectedActivity}
        config={config}
        members={members}
      />
    </>
  )
}

MeetingsContent.propTypes = {
  items: PropTypes.array.isRequired,
  sizes: PropTypes.object.isRequired,
  members: PropTypes.array,
  config: PropTypes.object,
  createOpen: PropTypes.bool,
  onCloseCreate: PropTypes.func
}

/* ── Notes ─────────────────────────────────────────────────────────── */

function formatNoteDate (isoString) {
  if (!isoString) return null
  return dayjs(isoString).format('M/D/YYYY')
}

function NoteCard ({ note, onClick, previewLength }) {
  const category = note.category || note.note_type_description || null
  const dateStr = formatNoteDate(note.created_at)

  return (
    <div
      role='button'
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } }}
      style={{
        backgroundColor: 'var(--color-neutral-100)',
        border: '1px solid var(--color-neutral-300)',
        borderRadius: '8px',
        padding: '12px',
        marginBottom: '8px',
        cursor: 'pointer'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
        <div style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          paddingTop: '2px',
          color: note.pinned ? 'var(--color-primary-400)' : 'var(--color-neutral-500)'
        }}
        >
          <Icon name={note.pinned ? 'pin' : 'unpin'} customSize='18px' />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {note._contact_name ? (
            <div style={{ fontSize: '11px', color: 'var(--text-color-subtle)', marginBottom: '2px' }}>
              {note._contact_name}
            </div>
          ) : null}
          {category ? (
            <div style={{ fontWeight: 600, fontSize: 'var(--font-size-xs)', marginBottom: '4px' }}>
              {category}
            </div>
          ) : null}
          <MarkupPreview
            type='html'
            length={previewLength}
            value={note.body || ''}
            className='font-size-sm'
          />
        </div>
        {dateStr ? (
          <div style={{
            flexShrink: 0,
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-color-subtle)',
            whiteSpace: 'nowrap'
          }}
          >
            {dateStr}
          </div>
        ) : null}
      </div>
    </div>
  )
}

NoteCard.propTypes = {
  note: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
  previewLength: PropTypes.number
}

NoteCard.defaultProps = {
  previewLength: 200
}

function NotesContent ({ data, members, config, createOpen, onCloseCreate }) {
  const [selectedNote, setSelectedNote] = useState(null)

  const pinned = data?.pinned ?? []
  const recent = data?.recent ?? []
  const previewLength = config?.notePreviewLength ?? 200

  const handleCloseDetail = useCallback(() => setSelectedNote(null), [])

  return (
    <>
      {pinned.length > 0 ? (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontWeight: 700, fontSize: 'var(--font-size-xs)', marginBottom: '6px', textTransform: 'uppercase' }}>
            Pinned
          </div>
          {pinned.map(note => (
            <NoteCard key={note.id} note={note} previewLength={previewLength} onClick={() => setSelectedNote(note)} />
          ))}
        </div>
      ) : null}

      <div>
        <div style={{ fontWeight: 700, fontSize: 'var(--font-size-xs)', marginBottom: '6px', textTransform: 'uppercase' }}>
          Recent
        </div>
        {recent.length > 0
          ? recent.map(note => (
            <NoteCard key={note.id} note={note} previewLength={previewLength} onClick={() => setSelectedNote(note)} />
          ))
          : (
            <FlexRow
              className='justify-center items-center'
              style={{
                minHeight: '60px',
                color: 'var(--text-color-subtle)',
                fontSize: 'var(--font-size-sm)'
              }}
            >
              No recent notes
            </FlexRow>
          )}
      </div>

      <CreateNoteModal
        open={createOpen}
        onClose={onCloseCreate}
        config={config}
        members={members}
        existingNotes={[...pinned, ...recent]}
      />

      <NoteDetailModal
        open={!!selectedNote}
        onClose={handleCloseDetail}
        note={selectedNote}
      />
    </>
  )
}

NotesContent.propTypes = {
  data: PropTypes.object,
  members: PropTypes.array,
  config: PropTypes.object,
  createOpen: PropTypes.bool,
  onCloseCreate: PropTypes.func
}

/* ── Workflows ────────────────────────────────────────────────────── */

const WORKFLOW_STATUS = {
  complete: { icon: 'checkCircle', color: 'var(--color-growth-500)' },
  overdue: { icon: 'clock', color: 'var(--color-decline-500)' },
  outstanding: { icon: 'radioUnchecked', color: 'var(--text-color-subtle)' }
}

function WorkflowRow ({ workflow, sizes, onClick }) {
  const status = getWorkflowStatus(workflow)
  const cfg = WORKFLOW_STATUS[status]
  const contactName = workflow.linked_contacts?.[0]?.full_name || null
  const dueStr = workflow.due_date ? dayjs(workflow.due_date).format('M/D/YYYY') : null

  return (
    <div
      role='button'
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } }}
      style={{ borderBottom: '1px solid var(--color-neutral-200)', padding: sizes.rowPadding, cursor: 'pointer' }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
        <div style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          paddingTop: '2px',
          color: cfg.color
        }}
        >
          <Icon name={cfg.icon} customSize='20px' />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {contactName ? (
            <div style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--text-color-subtle)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
            >
              {contactName}
            </div>
          ) : null}
          <div style={{
            fontSize: sizes.fontSize,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          >
            {workflow.name || 'Untitled Workflow'}
          </div>
        </div>
        {dueStr ? (
          <div style={{
            flexShrink: 0,
            textAlign: 'right',
            fontSize: 'var(--font-size-xs)',
            color: status === 'overdue' ? 'var(--color-decline-500)' : 'var(--text-color-subtle)',
            whiteSpace: 'nowrap'
          }}
          >
            {status === 'complete' ? 'Completed' : `Due ${dueStr}`}
          </div>
        ) : null}
      </div>
    </div>
  )
}

WorkflowRow.propTypes = {
  workflow: PropTypes.object.isRequired,
  sizes: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired
}

function WorkflowsContent ({ items, sizes }) {
  const queryClient = useQueryClient()
  const [selectedWorkflowId, setSelectedWorkflowId] = useState(null)

  useEffect(() => {
    for (const wf of items) {
      queryClient.setQueryData(
        workflowDetailKey(wf.id),
        old => old ?? wf
      )
    }
  }, [items, queryClient])

  const handleCloseDetail = useCallback(() => setSelectedWorkflowId(null), [])

  return (
    <>
      {!items.length
        ? (
          <FlexRow
            className='justify-center items-center'
            style={{
              minHeight: '80px',
              color: 'var(--text-color-subtle)',
              fontSize: 'var(--font-size-sm)'
            }}
          >
            No workflows found
          </FlexRow>
        )
        : items.map(workflow => (
          <WorkflowRow
            key={workflow.id}
            workflow={workflow}
            sizes={sizes}
            onClick={() => setSelectedWorkflowId(workflow.id)}
          />
        ))}

      <WorkflowDetailModal
        open={!!selectedWorkflowId}
        onClose={handleCloseDetail}
        workflowId={selectedWorkflowId}
      />
    </>
  )
}

WorkflowsContent.propTypes = {
  items: PropTypes.array.isRequired,
  sizes: PropTypes.object.isRequired
}

/* ── Main renderer ─────────────────────────────────────────────────── */

function RedtailListInner ({ config, styleVariant }) {
  const presetKey = config?.preset
  const { data, items, members, isLoading, notLinked, title, icon } = useRedtailListPreset(presetKey, config)
  const [modalOpen, setModalOpen] = useState(false)

  const sizes = SIZE_STYLES[styleVariant] || SIZE_STYLES.md
  const isNotesPreset = presetKey === 'latest-notes'
  const isWorkflowsPreset = presetKey === 'workflows'
  const showCreateButton = !isWorkflowsPreset

  const handleOpenModal = useCallback(() => setModalOpen(true), [])
  const handleCloseModal = useCallback(() => setModalOpen(false), [])

  if (!presetKey) {
    return (
      <div className='p-md font-size-sm' style={{ color: 'var(--color-warning-500)' }}>
        No preset configured for this list component.
      </div>
    )
  }

  if (isLoading) {
    return <Loading />
  }

  if (notLinked) {
    return (
      <FadeIn>
        <FlexRow className='justify-center items-center' style={{ minHeight: sizes.minHeight, color: 'var(--text-color-subtle)', fontSize: 'var(--font-size-xs)' }}>
          Client is not linked to Redtail
        </FlexRow>
      </FadeIn>
    )
  }

  const renderContent = () => {
    if (isNotesPreset) {
      return (
        <NotesContent
          data={data}
          members={members}
          config={config}
          createOpen={modalOpen}
          onCloseCreate={handleCloseModal}
        />
      )
    }
    if (isWorkflowsPreset) {
      return (
        <WorkflowsContent
          items={items}
          sizes={sizes}
        />
      )
    }
    return (
      <MeetingsContent
        items={items}
        sizes={sizes}
        members={members}
        config={config}
        createOpen={modalOpen}
        onCloseCreate={handleCloseModal}
      />
    )
  }

  return (
    <FadeIn>
      <div style={{ padding: '8px 12px' }}>
        <FlexRow style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {icon ? (
              <div style={{ display: 'flex', alignItems: 'center', fill: 'var(--color-primary-500)' }}>
                <Icon name={icon} customSize='24px' />
              </div>
            ) : null}
            <div style={{ fontSize: sizes.titleFontSize, fontWeight: 600 }}>
              {title}
            </div>
          </div>
          {showCreateButton ? (
            <SydButton
              size='sm'
              variant='outline'
              icon='add'
              onClick={handleOpenModal}
            >
              Create
            </SydButton>
          ) : null}
        </FlexRow>

        {renderContent()}
      </div>
    </FadeIn>
  )
}

RedtailListInner.propTypes = {
  config: PropTypes.object,
  styleVariant: PropTypes.string
}

function RedtailListRenderer ({ config, styleVariant, presentationKey, blueprint, displayName }) {
  const sizes = SIZE_STYLES[styleVariant] || SIZE_STYLES.md

  return (
    <div style={{ minHeight: sizes.minHeight }}>
      <IntegrationAuthProvider integration='redtail' size='sm' loadingType='circular'>
        <RedtailListInner
          config={config}
          styleVariant={styleVariant}
          presentationKey={presentationKey}
          blueprint={blueprint}
          displayName={displayName}
        />
      </IntegrationAuthProvider>
    </div>
  )
}

RedtailListRenderer.propTypes = {
  config: PropTypes.object,
  styleVariant: PropTypes.string,
  presentationKey: PropTypes.string,
  blueprint: PropTypes.object,
  displayName: PropTypes.string
}

export default RedtailListRenderer
